import React, {useEffect, useRef, useState} from "react";
import {useDispatch} from "react-redux";
import {useTranslation} from "react-i18next";
import {buildStyles} from "connectui/component/buildStyles";
import {useTheme} from "connectui/theme/ThemeProvider";
import ICONS from "connectui/icons/iconMap";
import useResizeObserver from "hooks/useResizeObserver";
import {satisfaction} from "configuration/constants";
import {updateGuest} from "redux/actions/guestsActions";
// Components
import FlexContainer from "connectui/component/layout/FlexContainer";
import ButtonIcon from "connectui/component/input/button/ButtonIcon";
import SvgIcon from "connectui/component/display/SvgIcon";
import TextInput from "connectui/component/input/text/type/TextInput";
import FilledInputBase from "connectui/component/input/text/FilledInputBase";
import Text from "connectui/component/display/Text";
import ProfilePicture from "connectui/component/display/ProfilePicture";
import SentimentField from "ui/component/field/fields/SentimentField";
import HeaderActions from "../../conversations/header/HeaderActions";
import Skeleton from "connectui/component/display/Skeleton";
import Container from "connectui/component/layout/Container";

const useStyles = (theme, isMobile) =>
  buildStyles({
    header: {width: "100%"},
    iconBtn: {
      borderRadius: theme.spacing(2),
      backgroundColor: theme.colors.white,
      padding: "0px",
      border: "none",
      outline: "none",
      "&:hover": {backgroundColor: theme.colors.hoverGray},
      "&.open": {backgroundColor: theme.colors.gray100},
    },
    icon: {
      width: "28px",
      height: "28px",
      justifyContent: "center",
      padding: "5px",
      color: theme.colors.textSecondary,
      "&.open": {color: theme.colors.gray900},
    },
    nameContainer: {
      "&:hover > .-edit-icon": {width: "16px", opacity: 1},
    },
    hoverIcon: {
      width: isMobile ? "16px" : 0,
      height: "16px",
      cursor: "pointer",
      transition: "opacity 0.15s",
      opacity: isMobile ? 1 : 0,
    },
    actionsIcon: {
      width: "18px",
      height: "18px",
      cursor: "pointer",
    },
    guestName: {
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      overflow: "hidden",
      width: "fit-content",
      marginRight: theme.spacing(2),
    },
  });

const GuestHeaderSection = ({
  guest,
  panelOpen,
  togglePanelOpen,
  loadingMessages,
  goBack,
}) => {
  const {theme, isMobile} = useTheme();
  const classes = useStyles(theme, isMobile);
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const containerRef = useRef(null);
  const [editName, setEditName] = useState(false);
  const [guestName, setGuestName] = useState("");
  const [updatedSentiment, setUpdatedSentiment] = useState(guest.rating);
  const headerSize = useResizeObserver(containerRef);
  let loading = loadingMessages || !guest.guest_id;

  useEffect(() => {
    setUpdatedSentiment(guest.rating);
  }, [guest]);

  const saveGuestName = () => {
    dispatch(
      updateGuest({
        guest_id: guest.guest_id,
        field: "name",
        val: guestName,
      }),
    );
    setEditName(false);
  };

  const handleSentimentChange = (newSelection) => {
    setUpdatedSentiment(satisfaction[newSelection].value);
    dispatch(
      updateGuest({
        guest_id: guest.guest_id,
        field: "rating",
        val: satisfaction[newSelection].value,
      }),
    );
  };

  return (
    <FlexContainer
      ref={containerRef}
      className={classes.header}
      alignItems="center"
    >
      <FlexContainer
        width="100%"
        p={theme.spacing(4, 2.5, 4, isMobile ? 0 : 4)}
        justifyContent="space-between"
        alignItems="center"
      >
        {!!guest.guest_id ? (
          <FlexContainer
            alignItems="center"
            sx={{overflow: "hidden", minWidth: "48px"}}
          >
            {isMobile && (
              <ButtonIcon
                icon={
                  <SvgIcon
                    component={ICONS["chevron-left"]}
                    sx={{
                      width: "16px",
                      height: "16px",
                      color: theme.colors.textPrimary,
                    }}
                  />
                }
                sx={{
                  backgroundColor: theme.colors.white,
                  boxShadow: "none",
                  padding: theme.spacing(3),
                  marginRight: theme.spacing(-1),
                  "&:hover": {
                    backgroundColor: theme.colors.white,
                  },
                }}
                onClick={goBack}
              />
            )}

            {/* Guest Info */}
            <ProfilePicture
              sx={{
                width: "48px",
                height: "48px",
                marginRight: theme.spacing(2),
              }}
              name={guest?.name ?? ""}
              skeleton={{enabled: loading}}
              textProps={{variant: "textLG", weight: "medium"}}
            />
            <FlexContainer
              flexDirection="column"
              sx={{flex: 1, overflow: "hidden"}}
            >
              {editName ? (
                <TextInput
                  inputBase={FilledInputBase}
                  inputProps={{
                    placeholder: t("guest_name_placehoder"),
                    value: guestName,
                    onChange: (e) => {
                      setGuestName(e.target.value);
                    },
                  }}
                  actions={
                    <FlexContainer
                      alignItems="center"
                      gap={theme.spacing(1)}
                      sx={{marginLeft: theme.spacing(3)}}
                    >
                      <SvgIcon
                        className={classes.actionsIcon}
                        component={ICONS.check}
                        sx={{color: theme.colors.textSecondary}}
                        onClick={saveGuestName}
                      />
                      <SvgIcon
                        className={classes.actionsIcon}
                        component={ICONS["small-x"]}
                        sx={{color: theme.colors.textSecondary}}
                        onClick={() => setEditName(false)}
                      />
                    </FlexContainer>
                  }
                />
              ) : (
                <FlexContainer
                  className={!isMobile ? classes.nameContainer : undefined}
                  alignItems="center"
                  gap={theme.spacing(1)}
                  sx={{width: "100%"}}
                >
                  <Text
                    variant="textLG"
                    weight="semibold"
                    className={classes.guestName}
                    skeleton={{
                      enabled: loading,
                      width: "120px",
                      height: "25px",
                    }}
                  >
                    {guest.name ?? ""}
                  </Text>
                  {!editName && !loadingMessages && (
                    <SvgIcon
                      className={`${classes.hoverIcon} -edit-icon`}
                      component={ICONS.edit}
                      sx={{color: theme.colors.textSecondary}}
                      onClick={() => {
                        setGuestName(guest.name ?? "");
                        setEditName(true);
                      }}
                    />
                  )}
                </FlexContainer>
              )}
              {loading ? (
                <Skeleton
                  width={"85px"}
                  height={"22px"}
                  style={{
                    marginTop: theme.spacing(1),
                    borderRadius: theme.spacing(4),
                  }}
                />
              ) : (
                <Container sx={{maxWidth: "100%", overflow: "hidden"}}>
                  <SentimentField
                    type="input"
                    variant="dropdown"
                    fieldData={{val: updatedSentiment}}
                    handleValueChange={handleSentimentChange}
                  />
                </Container>
              )}
            </FlexContainer>
          </FlexContainer>
        ) : (
          <div />
        )}

        {/* Action buttons */}
        <HeaderActions
          guest={guest}
          loading={loading}
          headerSize={headerSize}
        />

        <ButtonIcon
          icon={
            <SvgIcon
              component={ICONS["layout-sidebar-right"]}
              className={[classes.icon, panelOpen ? "open" : ""].join(" ")}
            />
          }
          className={[classes.iconBtn, panelOpen ? "open" : ""].join(" ")}
          onClick={togglePanelOpen}
        />
      </FlexContainer>
    </FlexContainer>
  );
};

export default GuestHeaderSection;
